//#region [IMPORT]
// === THIRD-PARTY ===
import React, { createContext, useEffect, useState } from 'react';

// ### MUI ###
import {
  Autocomplete,
  Box,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, createTheme } from '@mui/material/styles';
import { Cancel, CheckCircle, Gavel, UnfoldMoreOutlined } from '@mui/icons-material';
import { blue, green, pink } from '@mui/material/colors';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

const RULE_COMPARE_METHODS = {
  status: (a, b) => {
    return (a || '') < (b || '');
  },
  description: (a, b) => {
    return (a || '') < (b || '');
  },
};

//-----------------------------------------------------------------
export function MissingDocs(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { file } = props;

  const theme = useTheme();

  const [reqd_docs, setReqdDocs] = useState([]);
  const [reqd_docs_filtered, setReqdDocsFiltered] = useState([]);
  const [sort_header, setSortHeader] = useState('status');
  const [sort_ascend, setSortAscend] = useState(false); 

  const _tags = [
    { label: 'Passed', group: 'Status', filter: (rule) => rule.status === 1 },
    { label: 'Failed', group: 'Status', filter: (rule) => rule.status === 0 },
    { label: 'Overruled', group: 'Status', filter: (rule) => rule.status === 2 },
    { label: 'Unprocessed', group: 'Status', filter: (rule) => rule.status === undefined },
  ];

  const [ defaultFilters , setDefaultFilters ] = useState( getFilterTags() );

  //#endregion

  //#region [STYLES]

  const theme_local = createTheme({
    status: {
      passed: green[300],
      failed: pink[300],
    },
    palette: {
      hover: blue[200],
    },
  });

  const _styles = {
    card: {
      height: 'calc( 100% - 7rem )',
      width: '25%',
      maxWidth: '100%',
      overflowY: 'hidden',
      marginRight: '1rem',
      border: '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    control_container: {
      display: 'flex',
    },
    container_filter: {
      margin: '0 0.5rem',
    },
    filter_tags: {
      width: '16rem',
    },
    validate_button: {
      height: '4rem',
      margin: '0.5rem 0.5rem',
    },
    table_rules: {},
    table_header: {
      boxShadow: '0 0 0.5em 0.01em' + theme.palette.primary.main,
    },
    table_header_status: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor:
        sort_header === 'status' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_description: {
      cursor: 'pointer',
      backgroundColor:
        sort_header === 'description' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_comment: {
      cursor: 'pointer',
      backgroundColor:
        sort_header === 'comment' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_options: {
      backgroundColor: theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_container: {
      height: '100%',
      width: '100%',
      maxWidth: '100%',
      overflowY: 'hidden',
    },
    table_container2: {
      height: 'calc( 100% - 4rem )',
      width: '100%',
      maxWidth: '100%',
      padding: '0',
      overflowY: 'auto',
      scrollbarColor: `${theme.palette.background.secondary[theme.palette.mode]} ${
        theme.palette.background.primary[theme.palette.mode]
      }`,
    },
    task_queue_text: {
      display: 'flex',
      flexGrow: 1,
      margin: '1rem',
      fontSize: 'x-large',
      borderColor: 'primary',
    },
    table_cell: {
      borderBottom: 'none',
    },
    table_cell_status: {
      borderBottom: 'none',
      textAlign: 'center',
    },
    hover: {
      '&:hover': {
        backgroundColor: theme_local.palette.hover + ' !important',
      },
    },
    passed: {
      backgroundColor: theme.palette.success.main + ' !important',
    },
    failed: {
      backgroundColor: theme.palette.error.main + ' !important',
    },
  };

  // === ATTRIBUTES

  const CONTROL_CONTAINER = {
    sx: _styles.control_container,
  };

  const FILTER_CONTAINER = {
    sx: _styles.container_filter,
  };

  const FILTER_TAGS = {
    id: 'filter-tags',
    sx: _styles.filter_tags,
    multiple: true,
    options: _tags,
    defaultValue: defaultFilters,
    groupBy: (option) => option.group,
    renderInput: (params) => {
      return <TextField {...params} variant='standard' label='Filter Tags' />;
    },
    onChange: (event, value) => {
      applyFilters       ( value.map( (x) => x.filter ) );
      saveSelectedFilters( value.map( (x) => x.label  ) );
    },
  };

  const TABLE_HEADER_STATUS = {
    sx: _styles.table_header_status,
    sortDirection: sort_ascend ? 'asc' : 'desc',
    onClick: () => onClickHeader('status'),
    children: (
      <Box sx={{ display: 'flex' }}>
        <UnfoldMoreOutlined sx={{ alignItem: 'center' }} /> Passed
      </Box>
    ),
  };

  const TABLE_HEADER_DESCRIPTION = {
    sx: _styles.table_header_description,
    sortDirection: sort_ascend ? 'asc' : 'desc',
    onClick: () => onClickHeader('description'),
    children: (
      <Box sx={{ display: 'flex' }}>
        <UnfoldMoreOutlined sx={{ alignItem: 'center' }} /> Description
      </Box>
    ),
  };

  //#endregion

  //#region [EFFECT]

  //-----------------------------------------------------------------
  useEffect(() => {
    //-----------------------------------------------------------------
    let docs = file.rules
      .filter((r) => {
        return r.tags.is_required_document;
      })
      .sort((a, b) => a.status - b.status);
    setReqdDocs(docs);
    return () => {};
  }, [file.rules]);
  
  //-----------------------------------------------------------------
  useEffect(() => {
    //-----------------------------------------------------------------
    const tags = getFilterTags();

    setDefaultFilters( tags );
    applyFilters( tags.map( tag => tag.filter ) );
  }, [reqd_docs]);


  
  //#endregion
  
  //#region [METHOD]

  //-----------------------------------------------------------------
  function getFilterTags() {
    //-----------------------------------------------------------------
    let result = [];

    try {
      const saved_filters = loadSelectedFilters();
      result = _tags.filter( tag => saved_filters?.includes( tag.label ) );
    } catch (error) {
      console.warn( error );
    }
  
    return result;
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClickHeader(name) {
    //-----------------------------------------------------------------
    const sorted = reqd_docs_filtered.sort((a, b) => RULE_COMPARE_METHODS[name](a[name], b[name]));

    if (sort_header === name) {
      setSortAscend(!sort_ascend);
      if (sort_ascend) sorted.reverse();
    } else {
      setSortAscend(true);
      setSortHeader(name);
    }
    setReqdDocsFiltered(sorted);
  }

  //-----------------------------------------------------------------
  function applyFilters(filters) {
    //-----------------------------------------------------------------
    if ( filters.length > 0 ) {
      const filtered = reqd_docs.filter( (doc) => filters.some((filter) => filter(doc) ));
      setReqdDocsFiltered(filtered);
    } else {
      setReqdDocsFiltered(reqd_docs);
    }
  }
  
  //-----------------------------------------------------------------
  function saveSelectedFilters(filters) {
   //-----------------------------------------------------------------
    localStorage.setItem('da-requiredocs-filters', JSON.stringify(filters));
  }

  //-----------------------------------------------------------------
  function loadSelectedFilters() {
    //----------------------------------------------------------------- 
    const savedFilters = localStorage.getItem('da-requiredocs-filters');    
      return JSON.parse(savedFilters);    
  }
  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function displayData() {
    //-----------------------------------------------------------------
    return (
      <Box sx={_styles.card}>
        <TableContainer sx={_styles.table_container}>
          <Box {...CONTROL_CONTAINER}>
            <Typography sx={_styles.task_queue_text}>Required Documents</Typography>
            <Box {...FILTER_CONTAINER}>
              <Autocomplete {...FILTER_TAGS} />
            </Box>
          </Box>
          <TableContainer sx={_styles.table_container2}>
            <Table stickyHeader size='small' sx={_styles.table_rules}>
              <TableHead sx={_styles.table_header}>
                <TableRow>
                  <TableCell key={1} {...TABLE_HEADER_STATUS} />
                  <TableCell key={2} {...TABLE_HEADER_DESCRIPTION} />
                </TableRow>
              </TableHead>
              <TableBody>{displayDataItems()}</TableBody>
            </Table>
          </TableContainer>
        </TableContainer>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayDataItems() {
    //-----------------------------------------------------------------
    return reqd_docs_filtered.map((item) => {
      return (
        <TableRow hover classes={{ hover: _styles.hover }} key={item.id} id={item.id}>
          <TableCell key={1} sx={_styles.table_cell_status}>
            {item.review ? (
              <div />
            ) : item.status ? (
              item.status === 2 ? (
                <Gavel color='success' />
              ) : (
                <CheckCircle color='success' />
              )
            ) : (
              <Cancel color='error' />
            )}
          </TableCell>
          <TableCell key={2} sx={_styles.table_cell}>
            {' '}
            {item.description}{' '}
          </TableCell>
        </TableRow>
      );
    });
  }

  //#endregion

  return displayData();
}
